import { getJson } from '@/lib/api';

/**
 * Client types, fetchers and display helpers for Analytics → Accounting →
 * Programs. Mirrors the server contract (api/v3/profitability/program-report):
 * event contribution per program from the Income & Expense Statements.
 */

/** Where the Programs pages live; a program's detail is one segment below. */
export const PROGRAMS_PATH = '/analytics/accounting/programs';

/** The route pattern for the list and the detail (`:groupKey` optional). */
export const PROGRAMS_ROUTE = `${PROGRAMS_PATH}/:groupKey?`;

export function programDetailPath(groupKey: string, search = ''): string {
  return `${PROGRAMS_PATH}/${encodeURIComponent(groupKey)}${search}`;
}

/** The first year statements are consistent (decision §9.2); the server defaults to it too. */
export const PROGRAMS_DEFAULT_FROM = '2023-01-01';

export const PROGRAM_MODE = {
  all: 'all',
  inPerson: 'in_person',
  virtual: 'virtual',
} as const;
export type ProgramMode = (typeof PROGRAM_MODE)[keyof typeof PROGRAM_MODE];

export const PROGRAM_MODE_OPTIONS: readonly { key: ProgramMode; label: string }[] = [
  { key: PROGRAM_MODE.all, label: 'All' },
  { key: PROGRAM_MODE.inPerson, label: 'In person' },
  { key: PROGRAM_MODE.virtual, label: 'Virtual' },
];

/**
 * Minimum events a program needs to be listed. Low-volume programs (one or two
 * events) swing on a single job, so the list hides them by default.
 */
export const MIN_EVENTS = { all: '1', five: '5', ten: '10', twentyFive: '25' } as const;
export type MinEvents = (typeof MIN_EVENTS)[keyof typeof MIN_EVENTS];
export const DEFAULT_MIN_EVENTS: MinEvents = MIN_EVENTS.five;
const MIN_EVENTS_VALUES: readonly MinEvents[] = Object.values(MIN_EVENTS);

export const MIN_EVENTS_OPTIONS: readonly { key: MinEvents; label: string }[] = [
  { key: MIN_EVENTS.all, label: 'All' },
  { key: MIN_EVENTS.five, label: '5+' },
  { key: MIN_EVENTS.ten, label: '10+' },
  { key: MIN_EVENTS.twentyFive, label: '25+' },
];

/** `?min=` → a minimum, defaulting to 5 events. */
export function parseMinEvents(value: string | null): MinEvents {
  return MIN_EVENTS_VALUES.find((option) => option === value) ?? DEFAULT_MIN_EVENTS;
}

/** Programs with at least `min` events. */
export function withMinEvents<T extends { readonly events: number }>(rows: readonly T[], min: MinEvents): T[] {
  const floor = Number(min);
  return rows.filter((row) => row.events >= floor);
}

/** `?mode=` → a mode, defaulting to all. */
export function parseProgramMode(value: string | null): ProgramMode {
  return value === PROGRAM_MODE.inPerson || value === PROGRAM_MODE.virtual ? value : PROGRAM_MODE.all;
}

/** URL keys the Programs pages keep their state in. */
export const PROGRAMS_PARAM = {
  from: 'from',
  to: 'to',
  mode: 'mode',
  tab: 'tab',
  costView: 'cost',
  minEvents: 'min',
} as const;

/** The two views of the list page. */
export const PROGRAMS_TAB = {
  programs: 'programs',
  losers: 'losers',
} as const;
export type ProgramsTab = (typeof PROGRAMS_TAB)[keyof typeof PROGRAMS_TAB];

export function parseProgramsTab(value: string | null): ProgramsTab {
  return value === PROGRAMS_TAB.losers ? PROGRAMS_TAB.losers : PROGRAMS_TAB.programs;
}

/** Cost by category in dollars, or per participant. */
export const COST_VIEW = {
  total: 'total',
  perParticipant: 'per',
} as const;
export type CostView = (typeof COST_VIEW)[keyof typeof COST_VIEW];

export const CONTRIBUTION_NOTE =
  'Contribution: revenue less itemized event costs, commission and iPad fees. Shipping not itemized, materials bought on cards and staff time are not included yet.';
export const EARLY_YEARS_NOTE =
  '2023 and 2024 itemized fewer costs than 2025, so their margins read about 5 to 7 points high.';

export interface ProgramMetrics {
  readonly events: number;
  readonly participants: number;
  readonly revenue: number;
  readonly itemizedCost: number;
  readonly commission: number;
  readonly ipadFees: number;
  readonly contribution: number;
  readonly margin: number | null;
  readonly revenuePerParticipant: number | null;
  readonly contributionPerParticipant: number | null;
  readonly contributionPerRevenueDollar: number | null;
  readonly medianEventMargin: number | null;
  readonly belowTargetShare: number | null;
  readonly inPersonEvents: number;
  readonly itemizedInPersonEvents: number;
  readonly completeness: number | null;
}

export interface ProgramRow extends ProgramMetrics {
  readonly groupKey: string;
}

export interface ReportWindow {
  readonly from: string;
  readonly to: string;
}

export interface ProgramsReport {
  readonly window: ReportWindow;
  readonly mode: ProgramMode;
  readonly targetMargin: number;
  readonly programs: readonly ProgramRow[];
  readonly totals: ProgramMetrics;
}

export interface ProgramEventRow {
  readonly opportunityId: string;
  readonly opportunityName: string;
  readonly accountName: string;
  readonly statementId: string;
  readonly statementName: string;
  readonly program: string;
  readonly eventDate: string | null;
  readonly isVirtual: boolean;
  readonly participants: number | null;
  readonly revenue: number;
  readonly itemizedCost: number;
  readonly commission: number;
  readonly ipadFees: number;
  readonly contribution: number;
  readonly margin: number | null;
  readonly itemized: boolean;
}

export interface CategoryCost {
  readonly category: string;
  readonly cost: number;
  readonly perParticipant: number | null;
  readonly share: number;
  readonly events: number;
}

export interface QuarterMargin {
  readonly quarter: string;
  readonly events: number;
  readonly revenue: number;
  readonly contribution: number;
  readonly margin: number | null;
}

export interface ProgramDetail {
  readonly groupKey: string;
  readonly window: ReportWindow;
  readonly mode: ProgramMode;
  readonly targetMargin: number;
  readonly metrics: ProgramMetrics;
  readonly costByCategory: readonly CategoryCost[];
  readonly marginByQuarter: readonly QuarterMargin[];
  readonly events: readonly ProgramEventRow[];
}

export interface LosersReport {
  readonly window: ReportWindow;
  readonly mode: ProgramMode;
  readonly negativeEvents: number;
  readonly negativeContribution: number;
  readonly events: readonly ProgramEventRow[];
}

export interface ProgramsQuery {
  readonly from: string;
  readonly to: string;
  readonly mode: ProgramMode;
}

function queryParams(query: ProgramsQuery): Record<string, string> {
  return { from: query.from, to: query.to, mode: query.mode };
}

export async function readProgramsReport(query: ProgramsQuery, signal?: AbortSignal): Promise<ProgramsReport> {
  return getJson<ProgramsReport>('/accounting/programs', queryParams(query), signal);
}

export async function readProgramDetail(
  groupKey: string,
  query: ProgramsQuery,
  signal?: AbortSignal,
): Promise<ProgramDetail> {
  return getJson<ProgramDetail>(`/accounting/programs/${encodeURIComponent(groupKey)}`, queryParams(query), signal);
}

/** How many of the lowest-contribution events the losers view lists. */
export const LOSERS_LIMIT = 100;

export async function readProgramLosers(query: ProgramsQuery, signal?: AbortSignal): Promise<LosersReport> {
  return getJson<LosersReport>('/accounting/programs/losers', { ...queryParams(query), limit: LOSERS_LIMIT }, signal);
}

// ─── Display ─────────────────────────────────────────────────────────────────

const moneyWhole = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
const moneyCompact = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  notation: 'compact',
  maximumFractionDigits: 1,
});
const moneyCents = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 });

export const NO_VALUE = '—';

export function formatMoney(value: number): string {
  return moneyWhole.format(value);
}

export function formatMoneyCompact(value: number): string {
  return moneyCompact.format(value);
}

/** A per-unit dollar figure: whole dollars, or cents under $10. */
export function formatPerUnit(value: number | null): string {
  if (value === null) return NO_VALUE;
  return Math.abs(value) < 10 ? moneyCents.format(value) : moneyWhole.format(value);
}

/** 0.704 → "70%"; null → dash. */
export function formatPercent(value: number | null): string {
  return value === null ? NO_VALUE : `${Math.round(value * 100)}%`;
}

/** Contribution per dollar of revenue: 0.69 → "$0.69". */
export function formatPerDollar(value: number | null): string {
  return value === null ? NO_VALUE : moneyCents.format(value);
}

/** "84% of events itemized", or a plain note when a program has no in-person events. */
export function completenessText(metrics: Pick<ProgramMetrics, 'completeness'>): string {
  return metrics.completeness === null ? 'Virtual only' : `${formatPercent(metrics.completeness)} of events itemized`;
}

/** Below this share of itemized events, a program's numbers are flagged as thin. */
export const LOW_COMPLETENESS = 0.75;

export function isThin(metrics: Pick<ProgramMetrics, 'completeness'>): boolean {
  return metrics.completeness !== null && metrics.completeness < LOW_COMPLETENESS;
}

export const MARGIN_TONE = {
  loss: 'loss',
  below: 'below',
  healthy: 'healthy',
  none: 'none',
} as const;
export type MarginTone = (typeof MARGIN_TONE)[keyof typeof MARGIN_TONE];

/** A margin against the target: a loss, below target, or healthy. */
export function marginTone(margin: number | null, target: number): MarginTone {
  if (margin === null) return MARGIN_TONE.none;
  if (margin < 0) return MARGIN_TONE.loss;
  return margin < target ? MARGIN_TONE.below : MARGIN_TONE.healthy;
}

/** "2025-Q3" → "Q3 2025". */
export function quarterLabel(quarter: string): string {
  const [year, q] = quarter.split('-');
  return `${q ?? ''} ${year ?? ''}`.trim();
}

/** "2025-05-10" → "May 10, 2025". */
export function formatEventDay(iso: string | null): string {
  if (iso === null) return NO_VALUE;
  return new Date(`${iso}T00:00:00.000Z`).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

export { salesforceOpportunityUrl as opportunityUrl, salesforceStatementUrl as statementUrl } from './salesforce';

/** The window shown under the tiles: "Jan 1, 2023 to Oct 4, 2026". */
export function windowText(window: ReportWindow): string {
  return `${formatEventDay(window.from)} to ${formatEventDay(window.to)}`;
}
